/* ========================================================================
 * Loop Video Component
 * ======================================================================== */

( function( app, $ ) {
  'use strict';

  const COMPONENT = {
    className: 'loop-video',

    selector: function() {
      return '.' + this.className;
    },

    init: function() {
      /**
       * Get the component container associated with a video or button.
       *
       * @param {HTMLElement|jQuery} element
       * @return {jQuery}
       */
      const getContainer = function( element ) {
        return $( element ).closest( '.video-image-container' );
      };

      /**
       * Get the toggle button associated with a video.
       *
       * @param {HTMLElement|jQuery} video
       * @return {jQuery}
       */
      const getToggleButton = function( video ) {
        return getContainer( video )
            .find( '.loop-video-toggle-state' )
            .first();
      };

      /**
       * Get the video associated with a toggle button.
       *
       * @param {HTMLElement|jQuery} button
       * @return {jQuery}
       */
      const getVideo = function( button ) {
        return getContainer( button )
            .find( 'video.loop-video' )
            .first();
      };

      /**
       * Copy each source's data-src value into its src attribute.
       *
       * Returns true when at least one source was changed.
       *
       * @param {HTMLElement|jQuery} video
       * @return {boolean}
       */
      const switchSource = function( video ) {
        const $video = $( video );
        let sourceChanged = false;

        $video.find( 'source[data-src]' ).each( function() {
          const $source = $( this );
          const dataSrc = $source.attr( 'data-src' );

          if ( !$source.attr( 'src' ) && dataSrc ) {
            $source.attr( 'src', dataSrc );
            sourceChanged = true;
          }
        } );

        return sourceChanged;
      };

      /**
       * Fade out the fallback image.
       *
       * @param {HTMLElement|jQuery} video
       */
      const hideFallbackImage = function( video ) {
        getContainer( video )
            .find( '.loop-video-image' )
            .addClass( 'image-fade' );
      };

      /**
       * Show the fallback image.
       *
       * @param {HTMLElement|jQuery} video
       */
      const showFallbackImage = function( video ) {
        getContainer( video )
            .find( '.loop-video-image' )
            .removeClass( 'image-fade' );
      };

      /**
       * Update the component to show its playing state.
       *
       * @param {HTMLElement|jQuery} video
       */
      const setPlayingState = function( video ) {
        const $video = $( video );
        const $button = getToggleButton( $video );

        $video.removeClass( 'is-paused' );

        $button
            .removeClass( 'is-paused' )
            .attr( 'title', 'Pause looping video' )
            .attr( 'aria-label', 'Pause looping video' )
            .attr( 'aria-pressed', 'true' );

        hideFallbackImage( $video );
      };

      /**
       * Update the component to show its paused state.
       *
       * @param {HTMLElement|jQuery} video
       */
      const setPausedState = function( video ) {
        const $video = $( video );
        const $button = getToggleButton( $video );

        $video.addClass( 'is-paused' );

        $button
            .addClass( 'is-paused' )
            .attr( 'title', 'Play looping video' )
            .attr( 'aria-label', 'Play looping video' )
            .attr( 'aria-pressed', 'false' );
      };

      /**
       * Load and play a video.
       *
       * @param {HTMLElement|jQuery} video
       * @return {Promise<void>}
       */
      const playVideo = async function( video ) {
        const $video = $( video );
        const videoElement = $video.get( 0 );

        if ( !videoElement ) {
          return;
        }

        const sourceChanged = switchSource( $video );

        if ( sourceChanged || videoElement.readyState === 0 ) {
          videoElement.load();
        }

        try {
          await videoElement.play();
          setPlayingState( $video );
        } catch ( error ) {
          setPausedState( $video );
          console.error( 'Loop video could not play:', error );
        }
      };

      /**
       * Pause a video.
       *
       * @param {HTMLElement|jQuery} video
       */
      const pauseVideo = function( video ) {
        const $video = $( video );
        const videoElement = $video.get( 0 );

        if ( !videoElement ) {
          return;
        }

        videoElement.pause();
        setPausedState( $video );
      };

      /**
       * Get all lazy loop videos.
       */
      const $lazyVideos = $( 'video.loop-video.lazy:not(.delay)' );

      /**
       * Lazy-load videos when they enter the viewport.
       */
      if ( 'IntersectionObserver' in window ) {
        const lazyVideoObserver = new IntersectionObserver( function( entries ) {
          entries.forEach( function( entry ) {
            if ( !entry.isIntersecting ) {
              return;
            }

            const videoElement = entry.target;
            const $video = $( videoElement );
            const isDesktopVideo = $video.hasClass( 'desktop' );
            const isMobileVideo = $video.hasClass( 'mobile' );
            const isDesktopViewport = window.matchMedia(
                '(min-width: 768px)',
            ).matches;

            if ( isDesktopVideo && isDesktopViewport ) {
              switchSource( $video );

              if ( window.hasMotion ) {
                videoElement.load();
              }
            } else if ( isMobileVideo ) {
              switchSource( $video );
              videoElement.load();
            }

            $video.removeClass( 'lazy' );
            lazyVideoObserver.unobserve( videoElement );
          } );
        } );

        $lazyVideos.each( function() {
          lazyVideoObserver.observe( this );
        } );
      } else {
        /*
         * IntersectionObserver fallback.
         * Load sources immediately in older browsers.
         */
        $lazyVideos.each( function() {
          switchSource( this );
          this.load();
          $( this ).removeClass( 'lazy' );
        } );
      }

      /**
       * Keep button classes synchronized with the video's real state.
       */
      $( document ).on( 'play', 'video.loop-video', function() {
        setPlayingState( this );
      } );

      $( document ).on( 'pause', 'video.loop-video', function() {
        setPausedState( this );
      } );

      /**
       * Hide the fallback image after the video has usable data.
       */
      $( document ).on( 'loadeddata', 'video.loop-video', function() {
        const videoElement = this;

        if ( videoElement.readyState > 0 && !videoElement.paused ) {
          setPlayingState( videoElement );
        }
      } );

      /**
       * Show the fallback image if the video fails to load.
       */
      $( document ).on( 'error', 'video.loop-video', function() {
        setPausedState( this );
        showFallbackImage( this );

        console.error( 'Loop video failed to load:', this.currentSrc );
      } );

      /**
       * Play or pause the video associated with the clicked button.
       */
      $( document ).on(
          'click',
          '.loop-video-toggle-state',
          async function( event ) {
            event.preventDefault();

            const $button = $( this );
            const $video = getVideo( $button );
            const videoElement = $video.get( 0 );

            if ( !videoElement ) {
              console.error(
                  'No loop video was found in this video container.',
                  this,
              );
              return;
            }

            /*
           * Check the native paused property instead of relying only on
           * the is-paused CSS class.
           */
            if ( videoElement.paused || videoElement.ended ) {
              await playVideo( $video );
            } else {
              pauseVideo( $video );
            }
          },
      );

      /**
       * Handle desktop/mobile breakpoint behavior.
       */
      const desktopMediaQuery = window.matchMedia( '(min-width: 768px)' );

      const handleViewportChange = function( event ) {
        const $desktopVideos = $( 'video.loop-video.desktop' );

        if ( !$desktopVideos.length ) {
          return;
        }

        $desktopVideos.each( function() {
          const videoElement = this;
          const $video = $( videoElement );

          if ( event.matches ) {
            /*
             * Desktop viewport:
             * load and autoplay when motion is allowed.
             */
            if ( window.hasMotion ) {
              playVideo( $video );
            } else {
              pauseVideo( $video );
              showFallbackImage( $video );
            }
          } else {
            /*
             * Mobile viewport:
             * pause desktop-only videos and show their fallback images.
             */
            pauseVideo( $video );
            showFallbackImage( $video );
          }
        } );
      };

      handleViewportChange( desktopMediaQuery );

      if ( typeof desktopMediaQuery.addEventListener === 'function' ) {
        desktopMediaQuery.addEventListener(
            'change',
            handleViewportChange,
        );
      } else if ( typeof desktopMediaQuery.addListener === 'function' ) {
        /*
         * Safari and older browser fallback.
         */
        desktopMediaQuery.addListener( handleViewportChange );
      }

      /**
       * Initialize button accessibility and state.
       */
      $( '.loop-video-toggle-state' ).each( function() {
        const $button = $( this );
        const $video = getVideo( $button );
        const videoElement = $video.get( 0 );

        if ( !videoElement ) {
          return;
        }

        if ( videoElement.paused ) {
          setPausedState( $video );
        } else {
          setPlayingState( $video );
        }
      } );
    },

    finalize: function() {},
  };

  app.registerComponent( 'loop-video', COMPONENT );
} )( app, jQuery );
